import React, { useCallback } from 'react';
import { CanvasWidget, ToolType, WidgetGroup } from './types';
import Widget from './Widget';
import WidgetErrorBoundary from '../shared/WidgetErrorBoundary';
import { useWalletUser } from '../../hooks/useWalletUser';

interface WidgetLayerProps {
  widgets: CanvasWidget[];
  onWidgetUpdate: (widget: CanvasWidget) => void;
  onWidgetSelect?: (widgetId: string, isMultiSelect: boolean) => void;
  onWidgetHover?: (isHovering: boolean) => void;
  onWidgetGrab?: (widgetId: string) => void;
  onDuplicateWidget?: (widgetId: string) => void;
  onMaximizeWidget?: (widgetId: string) => void;
  onLockWidget?: (widgetId: string, isLocked: boolean) => void;
  onManageGroup?: () => void;
  tool: ToolType;
  zoom: number;
  widgetGroups?: WidgetGroup[];
  selectedWidgetIds?: string[];
}

const WidgetLayer: React.FC<WidgetLayerProps> = ({
  widgets,
  onWidgetUpdate,
  onWidgetSelect,
  onWidgetHover,
  onWidgetGrab,
  onDuplicateWidget,
  onMaximizeWidget,
  onLockWidget,
  onManageGroup,
  tool,
  zoom,
  widgetGroups = [],
  selectedWidgetIds = []
}) => {
  // Get the wallet address from the wallet user hook
  const walletUserData = useWalletUser();

  // Sort widgets by z-index (lower z-index renders first, higher z-index appears on top)
  const sortedWidgets = [...widgets].sort((a, b) => {
    // Primary sort by z-index (widgets with higher z-index appear on top)
    const aZIndex = a.zIndex || 0;
    const bZIndex = b.zIndex || 0;
    if (aZIndex !== bZIndex) return aZIndex - bZIndex;
    
    // Secondary sort: ensure widgets without groups come first
    if (!a.groupId && b.groupId) return -1;
    if (a.groupId && !b.groupId) return 1;
    
    // If both have groups, compare group IDs
    if (a.groupId && b.groupId) {
      if (a.groupId !== b.groupId) return a.groupId.localeCompare(b.groupId);
      
      // Within the same group, parent widgets come first
      if (a.isParentWidget && !b.isParentWidget) return -1;
      if (!a.isParentWidget && b.isParentWidget) return 1;
    }
    
    // Default sort by ID
    return a.id.localeCompare(b.id);
  });

  // Function to properly handle widget updates, including content restoration
  const handleWidgetUpdate = useCallback((updatedWidget: CanvasWidget) => {
    // Check if widget needs content restoration
    const needsRestore = 'needsContentRestoration' in updatedWidget && updatedWidget.needsContentRestoration;
    
    if (needsRestore) {
      // Remove the restoration flag
      const { needsContentRestoration, ...widgetWithoutFlag } = updatedWidget;
      
      // Recreate the widget based on type
      restoreWidgetContent(widgetWithoutFlag).then(restoredWidget => {
        if (restoredWidget) {
          onWidgetUpdate(restoredWidget);
        } else {
          // If restoration failed, just pass the update without the flag
          onWidgetUpdate(widgetWithoutFlag);
        }
      });
    } else {
      // Normal update with no restoration needed
      onWidgetUpdate(updatedWidget);
    }
  }, [onWidgetUpdate]);
  
  // Function to restore widget content based on widget type
  const restoreWidgetContent = async (widget: CanvasWidget): Promise<CanvasWidget | null> => {
    try {
      let newWidget = { ...widget };
      
      if (widget.type === 'advanced-charts') {
        // Dynamically import the chart component
        const chartsModule = await import('../../widgets/AdvancedCharts/AdvancedCharts');
        if (chartsModule.default) {
          const AdvancedCharts = chartsModule.default;
          newWidget.content = <AdvancedCharts isWidget={true} />;
          return newWidget;
        }
      } 
      // Add other widget types as needed
      
      return null; // Indicate restoration failure
    } catch (error) {
      console.error(`Failed to restore widget ${widget.id} of type ${widget.type}:`, error);
      return null;
    }
  };

  const handleWidgetRefresh = (widgetId: string) => {
    console.log('Refreshing widget:', widgetId);
    
    // Find the widget that needs refreshing
    const widgetToRefresh = widgets.find(w => w.id === widgetId);
    if (widgetToRefresh) {
      // Add the restoration flag to trigger content recreation
      const widgetWithFlag = {
        ...widgetToRefresh,
        needsContentRestoration: true
      };
      
      handleWidgetUpdate(widgetWithFlag);
    }
  };

  return (
    <div className="absolute inset-0">
      {sortedWidgets.map(widget => {
        // Find the widget group if this widget belongs to one
        const widgetGroup = widget.groupId 
          ? widgetGroups.find(g => g.id === widget.groupId) 
          : undefined;
        
        return (
          <WidgetErrorBoundary 
            key={widget.id}
            widgetType={widget.type}
            onRefresh={() => handleWidgetRefresh(widget.id)}
          >
            <Widget 
              widget={widget}
              onUpdate={handleWidgetUpdate}
              onSelect={onWidgetSelect}
              onHover={onWidgetHover}
              onGrab={onWidgetGrab}
              onDuplicateWidget={onDuplicateWidget}
              onMaximizeWidget={onMaximizeWidget}
              onLockWidget={onLockWidget}
              onManageGroup={onManageGroup}
              tool={tool}
              zoom={zoom}
              isSelected={selectedWidgetIds.includes(widget.id)}
              widgetGroup={widgetGroup}
            />
          </WidgetErrorBoundary>
        );
      })}
    </div>
  );
};

export default WidgetLayer;